import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MagnifyingGlass, Check, UsersThree, Spinner, ArrowUUpLeft } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Phase D — Merge picker opened from the customer Profile (EditClientModal).
 * Searches BOTH "New User" support entries and registered clients, lets staff
 * pick one or more, and merges them into the current profile's account.
 */
const MergeAccountsPanel = ({ client, userId, onClose, onMerged }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState({}); // id -> row
  const [merging, setMerging] = useState(false);
  const [mergedSources, setMergedSources] = useState([]);
  const [demergingId, setDemergingId] = useState(null);
  const tRef = useRef(null);

  const loadMerged = useCallback(async () => {
    if (!client?.id) return;
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/merged-sources`);
      setMergedSources(Array.isArray(data?.merged_sources) ? data.merged_sources : []);
    } catch (_) { setMergedSources([]); }
  }, [client?.id]);

  useEffect(() => { loadMerged(); }, [loadMerged]);

  const doDemerge = async (row) => {
    setDemergingId(row.id);
    try {
      await axios.post(`${API}/clients/demerge`, {
        target_id: client.id,
        source_id: row.id,
        actor_id: userId,
      });
      toast.success(`Demerged ${row.name} — split into a separate chat`);
      await loadMerged();
      onMerged && onMerged({ demerged: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Demerge failed');
    } finally {
      setDemergingId(null);
    }
  };

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (!q.trim()) { setResults([]); setSearching(false); return; }
    setSearching(true);
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/clients/merge-search`, {
          params: { query: q.trim(), exclude_id: client?.id },
        });
        setResults(Array.isArray(data) ? data : []);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q, client?.id]);

  const toggle = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id]; else next[row.id] = row;
      return next;
    });
  };

  const selectedList = useMemo(() => Object.values(selected), [selected]);

  const doMerge = async () => {
    if (selectedList.length === 0) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/clients/merge`, {
        target_id: client.id,
        source_ids: selectedList.map((r) => r.id),
        actor_id: userId,
      });
      toast.success(`Merged ${data.merged_count} entr${data.merged_count === 1 ? 'y' : 'ies'} • ${data.messages_moved} messages moved`);
      onMerged && onMerged(data);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" data-testid="merge-panel">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        <div className="bg-indigo-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UsersThree size={22} weight="bold" />
            <div>
              <h3 className="font-bold text-base leading-tight">Merge into {client?.username || 'this client'}</h3>
              <p className="text-[11px] text-indigo-100 leading-tight">Combine New User / duplicate chats into this account</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-indigo-700 rounded-full" data-testid="merge-close-btn">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
            <MagnifyingGlass size={18} className="text-gray-400 flex-shrink-0" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search New Users or clients by name / number / code…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="merge-search-input"
            />
            {searching && <Spinner size={16} className="animate-spin text-gray-400" />}
          </div>
          {selectedList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="merge-selected-chips">
              {selectedList.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-full">
                  {r.name}
                  <button onClick={() => toggle(r)} className="hover:text-indigo-900"><X size={12} weight="bold" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Phase D — Demerge: entries currently merged into this client. */}
          {mergedSources.length > 0 && (
            <div className="border-b border-gray-100 bg-amber-50/40" data-testid="merged-sources-section">
              <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                Currently merged into this account ({mergedSources.length})
              </div>
              {mergedSources.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2 border-b border-amber-100/60 last:border-b-0" data-testid={`merged-source-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{r.name}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${r.type === 'new_user' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {r.type === 'new_user' ? 'New User' : 'Client'}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">merged {r.at ? new Date(r.at).toLocaleDateString() : ''}</div>
                  </div>
                  <button
                    onClick={() => doDemerge(r)}
                    disabled={demergingId === r.id}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 px-2.5 py-1.5 rounded-lg disabled:opacity-50"
                    data-testid={`demerge-btn-${r.id}`}
                    title="Split this entry back out"
                  >
                    {demergingId === r.id ? <Spinner size={13} className="animate-spin" /> : <ArrowUUpLeft size={13} weight="bold" />}
                    Demerge
                  </button>
                </div>
              ))}
            </div>
          )}
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-gray-400">
              {q.trim() ? (searching ? 'Searching…' : 'No matches found') : 'Type to search New Users and clients'}
            </div>
          ) : (
            results.map((r) => {
              const isSel = !!selected[r.id];
              return (
                <button
                  key={r.id}
                  onClick={() => toggle(r)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 text-left hover:bg-gray-50 ${isSel ? 'bg-indigo-50' : ''}`}
                  data-testid={`merge-result-${r.id}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                    {isSel && <Check size={13} weight="bold" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{r.name}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${r.type === 'new_user' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {r.type === 'new_user' ? 'New User' : 'Client'}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {r.serial_number ? `Code ${r.serial_number}` : ''}{r.phone ? ` • ${r.phone}` : ''}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="p-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">{selectedList.length} selected</span>
          <button
            onClick={doMerge}
            disabled={selectedList.length === 0 || merging}
            className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-indigo-700 flex items-center gap-2"
            data-testid="merge-confirm-btn"
          >
            {merging && <Spinner size={15} className="animate-spin" />}
            {merging ? 'Merging…' : `Merge ${selectedList.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeAccountsPanel;
